import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useIntersection } from '@mantine/hooks';

import { useBackButtonToPrevRoute } from '@/hooks/useBackButtonToPrevRoute.tsx';
import { scrollToElement, useKeyboardLift } from '@/hooks/useKeyboardLift.ts';
import { routes } from '@/navigation/routes.tsx';
import { Header } from '@/components/Messanger/Message/Header/Header.tsx';
import MessagingController from '@/api/messaging/messaging.ts';
import { DialogLabels, MessageLabels, MessageTypes } from '@/enums/apiEnums.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';
import { Contact, MessageUI } from '@/api/messaging/types.ts';
import { useWebSocketStore } from '@/store/useWebSocketStore.ts';
import { useDialogsStore } from '@/store/useDialogsStore.ts';
import InputArea from '@/pages/Messenger/InputArea/InputArea.tsx';
import { useMessagesStore } from '@/store/useMessagesStore.ts';
import MessageList from '@/components/Messanger/Message/MessageList/MessageList.tsx';
import MessageListSkeleton from '@/components/Messanger/Message/MessageListSkeleton/MessageListSkeleton.tsx';
import loadingAnimationData from '@/components/common/svg/assets/loader_rounded.json';
import LottieAnimation from '@/animations/LottieAnimation.tsx';
import { updateMessageStatusByTempId } from '@/pages/Messenger/utils/common.ts';
import { makeId } from '@/utils/make-id.ts';
import { useProfileStore } from '@/store/useProfileStore.ts';

const BAR_HEIGHT = 56;
const MESSAGES_LIMIT: number = 150;

const Chat = () => {
  useBackButtonToPrevRoute(routes.messenger);

  const params = useParams<{ id: string }>();
  const contactId: string = params.id!;

  const [messagesApi, setMessagesApi] = useState<MessageUI[]>([]);
  const [contact, setContact] = useState<Contact | null>(null);
  const [isSystem, setIsSystem] = useState<boolean | undefined>(false);
  const [isPendingGift, setIsPendingGift] = useState<boolean>(false);
  const [isBait, setIsBait] = useState<boolean>(false);
  const [isGiftAccepted, setIsGiftAccepted] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [allMessagesLoaded, setAllMessagesLoaded] = useState<boolean>(false);
  const [fetchedLength, setFetchedLength] = useState<number>(MESSAGES_LIMIT);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>(
    undefined
  );
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState<
    string | null
  >(null);
  const { lastEvents } = useWebSocketStore();
  const {
    markDialogAsRead,
    updateDialogWithOutcomeMessage,
    updateDialogContactOnlineState
  } = useDialogsStore();
  const {
    clearInputMessage,
    inputMessages,
    replyToMessage,
    setReplyToMessage,
    clearReplyToMessage
  } = useMessagesStore();

  const { premium } = useProfileStore();

  const contentRef = useRef<HTMLDivElement>(null);
  const messagesBeginRef = useRef<HTMLDivElement | null>(null);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);

  const { ref: topSideRef, entry: topSideEntry } = useIntersection({
    threshold: 1
  });

  const { ref: bottomRef, entry: bottomEntry } = useIntersection({
    threshold: 1
  });

  useEffect(() => {
    lastEvents.lastMessage = undefined;
  }, []);

  useEffect(() => {
    if (messagesApi.length === 0) {
      setIsBait(false);
      return;
    }

    const newestMessage = messagesApi[0];

    setIsBait(
      newestMessage.type === MessageTypes.TYPE_BAIT && premium.isExpired
    );
  }, [messagesApi.length, messagesApi, premium.isExpired]);

  const currentReplyMessage = replyToMessage[contactId];
  // Reply message block height: padding (8px * 2) + content (~50px) = ~66px
  const replyMessageHeight = currentReplyMessage ? 70 : 0;

  const {
    offset,
    contentPaddingBottom,
    inputHandlers,
    textAreaRef,
    stableHeight
  } = useKeyboardLift({
    barHeight: BAR_HEIGHT,
    lastMessageRef,
    replyMessageHeight,
    contentRef
  });

  const handleReply = (message: MessageUI) => {
    setReplyToMessage(contactId, message);

    setHighlightedMessageId(message.create_micros);

    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  };

  const handleCancelReply = () => {
    clearReplyToMessage(contactId);

    if (highlightedMessageId) {
      setHighlightedMessageId(null);
    }
  };

  const scrollToMessage = (messageId: string) => {
    const messageElement = document.querySelector(
      `[data-message-id="${messageId}"]`
    );

    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });

      setHighlightedMessageId(messageId);

      setTimeout(() => {
        setHighlightedMessageId(null);
      }, 2000);
    }
  };

  const handleReplyClick = () => {
    const currentReply = replyToMessage[contactId];
    if (!currentReply?.create_micros) return;
    scrollToMessage(currentReply.create_micros);
  };

  const handleSend = async () => {
    if (!inputMessages[contactId].trim()) return;
    const tempId = makeId();

    const currentReplyMessage = replyToMessage[contactId];

    const newMessage: MessageUI = {
      create_micros: (Date.now() * 1000).toString(),
      text_body: {
        message: inputMessages[contactId].trim()
      },
      labels: [MessageLabels.LABEL_DELIVERED],
      type: MessageTypes.TYPE_TEXT,
      status: { id: tempId, isSending: true, isError: false },
      ...(currentReplyMessage && { reply_message: currentReplyMessage })
    };

    setMessagesApi((prevMessages) => [newMessage, ...prevMessages]);
    clearInputMessage(contactId);
    clearReplyToMessage(contactId);
    setHighlightedMessageId(null);
    setTimeout(() => {
      scrollToElement(messagesBeginRef, 'smooth');
    }, 50);

    try {
      const res = await MessagingController.createTextMessage(contactId, {
        text: newMessage.text_body?.message ?? '',
        reply_message_micros: currentReplyMessage?.create_micros
          ? Number(currentReplyMessage.create_micros)
          : undefined
      });

      updateDialogWithOutcomeMessage(contactId, newMessage);

      if (res.text_body?.message) {
        updateMessageStatusByTempId(setMessagesApi, tempId, {
          create_micros: res.create_micros,
          status: { id: tempId, isSending: false, isError: false }
        });
      }
    } catch (e) {
      updateMessageStatusByTempId(setMessagesApi, tempId, {
        status: { id: tempId, isSending: false, isError: true }
      });
      catchErrors(e, true);
    }
  };

  const handleResend = async (messageId: string) => {
    const messageToResend = messagesApi.find(
      (msg) => msg.status?.id === messageId
    );

    if (!messageToResend || !messageToResend.text_body?.message) return;

    updateMessageStatusByTempId(setMessagesApi, messageId, {
      status: { id: messageId, isSending: true, isError: false }
    });

    try {
      const res = await MessagingController.createTextMessage(contactId, {
        text: messageToResend.text_body.message
      });

      updateDialogWithOutcomeMessage(contactId, messageToResend);

      if (res.text_body?.message) {
        updateMessageStatusByTempId(setMessagesApi, messageId, {
          create_micros: res.create_micros,
          status: { id: messageId, isSending: false, isError: false }
        });
      }
    } catch (e) {
      updateMessageStatusByTempId(setMessagesApi, messageId, {
        status: { id: messageId, isSending: false, isError: true }
      });
      catchErrors(e, true);
    }
  };

  const fetchMessages = async (limit: number) => {
    if (isFetching || allMessagesLoaded) return;

    setIsFetching(true);

    try {
      const fetchedDialog = await MessagingController.getDialogMessages(
        contactId,
        { limit, nextPageToken }
      );

      if (!fetchedDialog) return;

      const fetchedDialogArray = Array.isArray(fetchedDialog.messages)
        ? fetchedDialog.messages
        : [];

      if (fetchedDialogArray.length === 0) return;

      if (fetchedDialog.messages.length < limit) {
        setAllMessagesLoaded(true);
        setFetchedLength(fetchedDialog.messages.length);
      }

      setMessagesApi((prevMessages) => [
        ...prevMessages,
        ...fetchedDialog.messages
      ]);

      setTimeout(() => {
        lastMessageRef && scrollToElement(lastMessageRef, 'auto');
      }, 10);

      if (!contact) {
        setContact(fetchedDialog.contact);
        updateDialogContactOnlineState(
          contactId,
          fetchedDialog.contact.online_state
        );
      }

      if (!isSystem) {
        setIsSystem(
          fetchedDialog.dialog.labels?.includes(
            DialogLabels.LABEL_SYSTEM_CHANNEL
          ) ?? false
        );
      }

      if (!isPendingGift) {
        setIsPendingGift(
          fetchedDialog.dialog.labels?.includes(
            DialogLabels.LABEL_ACTION_REQUIRED
          ) ?? false
        );
      }

      setNextPageToken(fetchedDialog.next_page_token);
    } catch (e) {
      catchErrors(e, true);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchMessages(MESSAGES_LIMIT).catch((e) => {
      catchErrors(e, true);
    });

    MessagingController.readAllMessages(contactId).catch((e) => {
      catchErrors(e, true);
    });

    scrollToElement(messagesBeginRef, 'auto');
    markDialogAsRead(contactId);
  }, []);

  useEffect(() => {
    if (isGiftAccepted) {
      const newStickerMessage: MessageUI = {
        labels: [MessageLabels.LABEL_DELIVERED],
        create_micros: (Date.now() * 1000).toString(),
        type: MessageTypes.TYPE_STICKER,
        sticker_body: {
          sticker_id: 1
        }
      };

      setMessagesApi((prevMessages) => [newStickerMessage, ...prevMessages]);

      setTimeout(() => {
        scrollToElement(messagesBeginRef);
      }, 0);

      setIsPendingGift(false);
    }
  }, [isGiftAccepted]);

  useEffect(() => {
    if (topSideEntry?.isIntersecting) {
      void fetchMessages(MESSAGES_LIMIT);
    }
  }, [topSideEntry]);

  useEffect(() => {
    if (bottomEntry?.isIntersecting && hasUnreadMessages) {
      MessagingController.readAllMessages(contactId).catch((e) => {
        catchErrors(e);
      });
      markDialogAsRead(contactId);
      setHasUnreadMessages(false);
    }
  }, [bottomEntry]);

  useEffect(() => {
    if (lastEvents.lastMessage?.contact?.hid === contactId) {
      setMessagesApi((prevMessages) => {
        const updatedMessages = [
          ...(lastEvents.lastMessage?.message
            ? [lastEvents.lastMessage.message]
            : []),
          ...prevMessages
        ];

        if (!bottomEntry?.isIntersecting) {
          setHasUnreadMessages(true);
        } else {
          MessagingController.readAllMessages(contactId).catch((e) => {
            catchErrors(e);
          });
          markDialogAsRead(contactId);

          setTimeout(() => {
            scrollToElement(messagesBeginRef);
          }, 0);
        }

        return updatedMessages;
      });
    }

    return () => {
      // reset last message
      lastEvents.lastMessage = undefined;
    };
  }, [lastEvents.lastMessage]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        height: stableHeight,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        overscrollBehavior: 'none'
      }}
    >
      <Header
        hid={contactId}
        avatar={contact?.photo_url}
        onlineState={contact?.online_state}
        name={contact ? contact.name : ''}
        isEmptyMessages={messagesApi.length === 0}
        isSystem={isSystem}
      />

      <div
        ref={contentRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6), rgba(255,255,255,0.6)), url('/chat-bg.jpeg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          marginTop: 100,
          paddingBottom: isBait ? '140px' : 0,
          marginBottom: isBait ? 0 : '20px'
        }}
      >
        <div
          style={{
            minHeight: `calc(100% - ${contentPaddingBottom + 16}px)`,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            padding: '16px 0px',
            paddingBottom: contentPaddingBottom
          }}
        >
          {!allMessagesLoaded && (
            <div ref={topSideRef}>
              {messagesApi.length === 0 && <MessageListSkeleton />}
              {isFetching && messagesApi.length != 0 && (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <LottieAnimation
                    animationData={loadingAnimationData}
                    loop={true}
                    width={30}
                    height={30}
                  />
                </div>
              )}
            </div>
          )}
          <MessageList
            messages={messagesApi}
            fetchedLength={fetchedLength}
            lastMessageRef={lastMessageRef}
            contact={contact}
            onResend={handleResend}
            onReply={handleReply}
            highlightedMessageId={highlightedMessageId}
            onReplyMessageClick={scrollToMessage}
            contentRef={contentRef}
          />
          <div style={{ width: 10, height: 10 }} ref={messagesBeginRef} />
          <div ref={bottomRef} />
        </div>
      </div>

      <InputArea
        isSystem={isSystem}
        textAreaRef={textAreaRef}
        hasMessages={messagesApi.length !== 0}
        isPendingGift={isPendingGift}
        isBait={isBait}
        contactId={contactId}
        contactName={contact ? contact.name : ''}
        setIsGiftAccepted={setIsGiftAccepted}
        onFocus={inputHandlers.onFocus}
        onBlur={inputHandlers.onBlur}
        handleSend={handleSend}
        offset={offset}
        barHeight={BAR_HEIGHT}
        replyToMessage={replyToMessage[contactId]}
        onCancelReply={handleCancelReply}
        onReplyClick={handleReplyClick}
      />
    </div>
  );
};

export default Chat;
